import { Checkbox, ClassesIllustration } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { PersonLabelSource, PersonLabelType, PersonListType } from 'components/Persons/types/personTypes';
import { personHasLabel } from 'components/Persons/utils/personUtils';
import { ReactNode, useEffect, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtualizer } from '../../../Virtualizer/Virtualizer';
import styles from './PersonsList.module.scss';
import { PersonsListEntry } from './PersonsListEntry';

const PersonsListFragment = graphql(`
  fragment PersonsListFields on Query {
    labels: personLabels(schoolYearUuid: $schoolyearUuid, context: $labelContext, uuids: $labelUuids) {
      __typename
      context
      items {
        __typename
        uuid
        labels {
          __typename
          uuid
          source
          sourceUuids
          value
          subValue
          tooltip
        }
      }
    }
  }
`);

type PersonsListProps = {
  persons: PersonListType[];
  selectedLabels: PersonLabelType[];
  filterMode: Record<PersonLabelSource, 'and' | 'or'>;
  search?: string;
  activeSources: PersonLabelSource[];
  selectedPersons?: string[];
  padding?: 's' | 'm';
  showBirthdays?: boolean;
  emptyStatePadding?: 'none' | 's' | 'm' | 'l' | 'xl';
  onGetActions?: (uuid: string) => ReactNode;
  onSelectCheckbox?: (uuid: string) => void;
  onSelectRow?: (uuid: string) => void;
  onSelectAll?: (uuids: string[]) => void;
  onLabelClick: (label: PersonLabelType) => void;
  onCountChanged?: (current: number, max: number) => void;
  data: FragmentType<typeof PersonsListFragment> | undefined;
  scrollContainer: React.RefObject<HTMLDivElement> | null;
};

export const PersonsList = ({
  persons,
  selectedLabels,
  search,
  activeSources,
  selectedPersons,
  padding,
  showBirthdays,
  emptyStatePadding = 'l',
  onGetActions,
  onSelectCheckbox,
  onSelectRow,
  onSelectAll,
  onLabelClick,
  onCountChanged,
  data,
  scrollContainer,
  filterMode,
}: PersonsListProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(PersonsListFragment, data);

  const labels: PersonLabelType[] = useMemo(
    () =>
      (fragment?.labels.items ?? []).flatMap((person) => {
        const labels = person.labels;
        return labels.map((label) => ({
          uuid: label.uuid,
          personUuid: person.uuid,
          source: label.source,
          sourceUuids: label.sourceUuids,
          value: label.value,
          subValue: label.subValue ?? null,
          tooltip: label.tooltip ?? null,
        }));
      }),
    [fragment?.labels.items],
  );

  const uniqueSelectAllId = useId();

  const processedPersons = useMemo(() => {
    const activeSourcesSet = new Set(activeSources);

    const getLabelsForPerson = (uuid: string): PersonLabelType[] => {
      return labels.filter((label) => label.personUuid === uuid && activeSourcesSet.has(label.source)) ?? [];
    };

    const filtered =
      selectedLabels.length === 0
        ? persons
        : persons.filter((person) => {
            const personLabels = getLabelsForPerson(person.uuid);

            const andLabels = selectedLabels.filter((label, index, array) => {
              const filterModeForSource = filterMode[label.source];
              return filterModeForSource === 'and';
            });

            const orLabels = selectedLabels.filter((label) => {
              const filterModeForSource = filterMode[label.source];
              return filterModeForSource === 'or';
            });

            return (
              (andLabels.length > 0 ? andLabels.every((label) => personHasLabel(label, personLabels)) : true) &&
              (orLabels.length > 0 ? orLabels.some((label) => personHasLabel(label, personLabels)) : true)
            );
          });

    return filtered
      .filter((profile) => {
        if (!search || search === '') {
          return true;
        }
        const searchValue = search.toLowerCase();
        return (
          profile.listName?.toLowerCase().includes(searchValue) ||
          profile.lastName?.toLowerCase().includes(searchValue) ||
          profile.firstName?.toLowerCase().includes(searchValue) ||
          profile.email?.toLowerCase().includes(searchValue)
        );
      })
      .map((p) => {
        const labels = getLabelsForPerson(p.uuid);
        return {
          ...p,
          labels: labels,
        };
      });
  }, [activeSources, labels, persons, selectedLabels, search, filterMode]);

  useEffect(() => {
    onCountChanged && onCountChanged(processedPersons.length, persons.length);
  }, [processedPersons.length, onCountChanged, persons.length]);

  return (
    <div className={styles['persons-list']}>
      <Virtualizer
        options={{
          count: processedPersons.length,
          overscan: 5,
          getItemKey: (i) => {
            const person = processedPersons[i];
            return person.uuid + '-' + person.updatedAt + activeSources.join(',');
          },
          estimateSize: (i) => {
            const { innerWidth: width } = window;
            if (width < 1024) {
              return 130;
            }
            const person = processedPersons[i];
            if (!person.birthday && !person.email) {
              return 84;
            }
            return 103;
          },
          measureElement: (element) => {
            return element.getBoundingClientRect().height;
          },
          getScrollElement: () => (scrollContainer ? scrollContainer.current : null),
        }}
        actions={
          onSelectCheckbox || onSelectRow ? (
            <div className={styles.actions}>
              <Checkbox
                name={`select-all-${uniqueSelectAllId}`}
                label={
                  (selectedPersons ?? []).length >= processedPersons.length
                    ? t('common.deselectAll')
                    : t('common.selectAll')
                }
                checked={(selectedPersons ?? []).length >= processedPersons.length}
                onChange={() => onSelectAll && onSelectAll(processedPersons.map((p) => p.uuid))}
                className={styles['select-all']}
              />
              <div className={styles.left}>
                <div className={styles.info}>
                  {t('persons.labels.selectCount', {
                    current: (selectedPersons ?? []).length,
                    max: processedPersons.length,
                  })}
                </div>
              </div>
            </div>
          ) : undefined
        }
        emptyStateSettings={{ icon: <ClassesIllustration />, padding: emptyStatePadding, title: t('persons.noPerson') }}
      >
        {(virtualizer) => {
          return (
            <div className={styles['list-wrapper']}>
              {virtualizer.getVirtualItems().map((item) => {
                const person = processedPersons[item.index];
                return (
                  <PersonsListEntry
                    measureElementIndex={item.index}
                    key={item.key}
                    personUuid={person.uuid}
                    name={person.listName}
                    email={person.email}
                    birthday={person.birthday}
                    labels={person.labels}
                    actions={onGetActions && onGetActions(person.uuid)}
                    status={person.userStatus}
                    selected={selectedPersons?.includes(person.uuid)}
                    padding={padding}
                    showBirthdays={showBirthdays}
                    onSelectCheckbox={onSelectCheckbox ? (uuid) => onSelectCheckbox(uuid) : undefined}
                    onSelectRow={onSelectRow ? (uuid) => onSelectRow(uuid) : undefined}
                    onLabelClick={(label) => onLabelClick(label)}
                    measureElement={virtualizer.measureElement}
                  />
                );
              })}
            </div>
          );
        }}
      </Virtualizer>
    </div>
  );
};
